import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import type { ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { MemberSelect } from '@/features/members';
import { REVIEW_STATUSES } from '../constants';
import type { PerformanceReviewInput } from '../types';

const schema = z.object({
  employee: z.string().min(1, 'Employee is required'),
  reviewer: z.string(),
  period: z.string().max(50),
  rating: z.string(),
  summary: z.string(),
  status: z.enum(['draft', 'submitted', 'acknowledged']),
  review_date: z.string(),
  action_items: z.array(z.object({ title: z.string(), is_done: z.boolean() })),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'employee', 'reviewer', 'period', 'rating', 'summary', 'status', 'review_date',
] as const;

interface ReviewFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  /** Show the seed action-items editor (create only — edited on the detail page). */
  showActionItems?: boolean;
  onSubmit: (values: PerformanceReviewInput) => Promise<unknown>;
  onCancel: () => void;
}

export function ReviewForm({
  defaultValues,
  submitLabel,
  showActionItems = false,
  onSubmit,
  onCancel,
}: ReviewFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      reviewer: '',
      period: '',
      rating: '',
      summary: '',
      status: 'draft',
      review_date: '',
      action_items: [],
      ...defaultValues,
    },
  });

  const actionItems = useFieldArray({ control: form.control, name: 'action_items' });

  async function handleSubmit(values: FormValues) {
    const ratingNum = values.rating.trim() === '' ? null : Number(values.rating);
    if (ratingNum !== null && (Number.isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5)) {
      form.setError('rating', { message: 'Rating must be 1–5.' });
      return;
    }
    const input: PerformanceReviewInput = {
      employee: Number(values.employee),
      reviewer: values.reviewer ? Number(values.reviewer) : null,
      period: values.period.trim(),
      rating: ratingNum,
      summary: values.summary,
      status: values.status,
      review_date: values.review_date || null,
      action_items: showActionItems
        ? values.action_items
            .map((it) => ({ title: it.title.trim(), is_done: it.is_done }))
            .filter((it) => it.title)
        : undefined,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        <FormSection variant="card" title="Review" description="Who is being reviewed and for what period.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="employee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="review-employee-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchEmployees}
                      resolveLabel={resolveEmployeeLabel}
                      placeholder="Select an employee"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reviewer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reviewer</FormLabel>
                  <FormControl>
                    <MemberSelect
                      queryKey="review-reviewer-picker"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Unassigned"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="period"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Period</FormLabel>
                <FormControl>
                  <Input placeholder="2025 Q4" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Assessment" description="Score, status, and date.">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rating (1–5)</FormLabel>
                  <FormControl>
                    <Input type="number" min="1" max="5" step="1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REVIEW_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="review_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Review date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Summary" description="Narrative summary.">
          <FormField
            control={form.control}
            name="summary"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Summary</FormLabel>
                <FormControl>
                  <MarkdownEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Strengths, areas to grow, goals for next period…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {showActionItems ? (
          <FormSection
            variant="card"
            title="Action items"
            description="Follow-ups coming out of this review — tick any already done."
          >
            <div className="space-y-2">
              {actionItems.fields.map((row, index) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Controller
                    control={form.control}
                    name={`action_items.${index}.is_done`}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="accent-primary size-4 shrink-0"
                        aria-label="Done"
                      />
                    )}
                  />
                  <Input
                    {...form.register(`action_items.${index}.title`)}
                    placeholder="e.g. Lead a cross-team project"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => actionItems.remove(index)}
                    aria-label="Remove action item"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => actionItems.append({ title: '', is_done: false })}
              >
                <Plus className="size-4" /> Add item
              </Button>
            </div>
          </FormSection>
        ) : null}

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
